import { motion, AnimatePresence } from "framer-motion";
import { Plus, RefreshCw } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useStoreSettings } from "../context/StoreSettingsContext";
import { WishlistButton } from "./WishlistButton";
import { FlashBadge } from "./FlashBadge";
import { Stars } from "./Stars";
import { flashInfo, LOW_STOCK } from "../lib/flash";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: (i % 4) * 0.08 } }),
};

const euro = (n) => `€${n.toFixed(2).replace(".", ",")}`;

export const ProductGrid = ({ products, onOpen }) => {
  const { addItem } = useCart();
  const { canShop } = useStoreSettings();

  if (products.length === 0) {
    return (
      <div className="grid place-items-center rounded-2xl border border-dashed border-ink/15 py-24 text-center" data-testid="product-empty">
        <p className="font-serif text-2xl text-ink">Nessun prodotto in questa categoria</p>
        <p className="mt-2 text-sm text-ink-soft">Prova a selezionare un'altra sezione.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-5 sm:grid-cols-2 lg:grid-cols-3 md:gap-8">
      <AnimatePresence mode="popLayout">
        {products.map((p, i) => {
          const fi = flashInfo(p);
          return (
          <motion.article
            key={p.id}
            layout
            variants={reveal}
            custom={i}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: 12 }}
            className="group relative flex flex-col"
            data-testid={`product-card-${p.id}`}
          >
            <button
              onClick={() => onOpen(p)}
              className="clip-frame relative aspect-[4/5] rounded-2xl bg-panel"
              data-testid={`product-image-${p.id}`}
            >
              <img src={p.image} alt={p.name} className="h-full w-full object-contain bg-white p-3" />
              <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
                {fi.active && <FlashBadge end={fi.end} discount={fi.discount} />}
                {p.recurring && (
                  <span className="flex items-center gap-1 rounded-full bg-cream/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-electric backdrop-blur">
                    <RefreshCw className="h-3 w-3" strokeWidth={2} /> Mensile
                  </span>
                )}
                {p.is_new && (
                  <span className="rounded-full bg-lime px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-cream" data-testid={`new-badge-${p.id}`}>
                    Novità
                  </span>
                )}
              </div>
              {p.available === false && (
                <span className="absolute inset-x-3 bottom-3 rounded-full bg-terra py-1 text-center text-[10px] font-semibold uppercase tracking-wider text-cream" data-testid={`unavailable-${p.id}`}>
                  Esaurito
                </span>
              )}
            </button>
            <WishlistButton product={p} className="absolute right-3 top-3 bg-cream/90 shadow-sm" />

            <div className="mt-4 flex flex-1 flex-col">
              <button onClick={() => onOpen(p)} className="text-left">
                <h4 className="font-serif text-xl leading-snug text-ink">{p.name}</h4>
              </button>
              <p className="mt-1 line-clamp-2 text-sm text-ink-soft">{p.description}</p>
              {p.rating_count > 0 && <div className="mt-1.5"><Stars value={p.rating} count={p.rating_count} size="h-3.5 w-3.5" /></div>}
              <div className="mt-3 flex items-center justify-between gap-2">
                <span className="text-base font-semibold text-electric" data-testid={`product-price-${p.id}`}>
                  {fi.active ? (
                    <>
                      <span className="text-terra">{euro(fi.price)}</span>{" "}
                      <span className="text-xs font-normal text-ink-soft line-through">{euro(fi.original)}</span>
                      {p.lowest_price_30d != null && (
                        <span className="block text-[10px] font-normal leading-tight text-ink-soft" data-testid={`omnibus-${p.id}`}>
                          Prezzo più basso ultimi 30 gg: {euro(p.lowest_price_30d)}
                        </span>
                      )}
                    </>
                  ) : (
                    <>{euro(p.price)}</>
                  )}
                </span>
                {p.stock != null && (
                  <span className={`shrink-0 text-[11px] font-medium ${p.stock <= LOW_STOCK ? "text-amber" : "text-ink-soft"}`} data-testid={`stock-${p.id}`}>
                    Pezzi disponibili: {p.stock}
                  </span>
                )}
                {canShop ? (
                  <button
                    onClick={() => addItem({ ...p, price: fi.active ? fi.price : p.price })}
                    data-testid={`add-to-cart-${p.id}`}
                    aria-label={`Aggiungi ${p.name}`}
                    className="grid h-10 w-10 place-items-center rounded-full bg-sage text-cream transition-transform hover:scale-110 hover:bg-sage-deep active:scale-90"
                  >
                    <Plus className="h-4 w-4" strokeWidth={2} />
                  </button>
                ) : (
                  <span data-testid={`coming-soon-${p.id}`} className="rounded-full border border-ink/15 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-soft">
                    Prossimamente
                  </span>
                )}
              </div>
            </div>
          </motion.article>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
